import {
  type SignedLocalAuthorityEnvelope,
  verifySignedLocalAuthorityEnvelope,
} from '../../local-authority';
import type { FeatureAvailability } from './use-feature-gate';

export { canonicalStringify } from '../../local-authority';

export type FeatureEntitlementStatus =
  | 'missing'
  | 'valid'
  | 'expired'
  | 'invalid'
  | 'online';

export interface FeatureEntitlementPayload {
  version: 1;
  entitlementRevision: 1;
  subjectType: 'workspace' | 'plan';
  subjectId: string;
  workspaceId?: string;
  userId?: string;
  plan: string;
  features: FeatureAvailability[];
}

export interface SignedFeatureEntitlementPayload extends FeatureEntitlementPayload {
  issuedAt: string;
  expiresAt: string;
}

export type SignedFeatureEntitlement =
  SignedLocalAuthorityEnvelope<FeatureEntitlementPayload>;

export type ExpectedFeatureEntitlementSubject =
  | {
      subjectType: 'workspace';
      workspaceId: string;
      userId: string;
      /**
       * Current backend-resolved plan when available. Offline startup can load
       * a still-valid 48h workspace entitlement before quota/billing refresh
       * resolves; in that case the signed payload's plan is the grace-period
       * source of truth.
       */
      plan?: string | null;
    }
  | { subjectType: 'plan'; plan: string };

export interface FeatureEntitlementVerification {
  status: Exclude<FeatureEntitlementStatus, 'online'>;
  features: FeatureAvailability[];
  plan: string | null;
  expiresAt: string | null;
}

export function normalizeFeatureEntitlementPlan(plan: unknown): string | null {
  return typeof plan === 'string' && plan.trim().length > 0
    ? plan.trim().toLowerCase()
    : null;
}

export function hasKnownFeaturePlanConflict(
  expectedPlan: string | null | undefined,
  actualPlan: unknown
): boolean {
  const normalizedExpected = normalizeFeatureEntitlementPlan(expectedPlan);
  const normalizedActual = normalizeFeatureEntitlementPlan(actualPlan);
  return (
    normalizedExpected !== null &&
    normalizedActual !== null &&
    normalizedExpected !== normalizedActual
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isFeatureAvailability(value: unknown): value is FeatureAvailability {
  if (!isRecord(value)) return false;
  return (
    typeof value.featureKey === 'string' &&
    typeof value.displayName === 'string' &&
    (value.category === undefined || typeof value.category === 'string') &&
    typeof value.allowed === 'boolean' &&
    typeof value.configured === 'boolean' &&
    (typeof value.requiredPlan === 'string' || value.requiredPlan === null) &&
    (value.visibility === 'visible' || value.visibility === 'hidden')
  );
}

function isValidIsoDate(value: unknown): value is string {
  return typeof value === 'string' && Number.isFinite(Date.parse(value));
}

function isSignedFeatureEntitlementPayload(
  payload: unknown
): payload is SignedFeatureEntitlementPayload {
  if (!isRecord(payload)) return false;
  return (
    payload.version === 1 &&
    payload.entitlementRevision === 1 &&
    (payload.subjectType === 'workspace' || payload.subjectType === 'plan') &&
    typeof payload.subjectId === 'string' &&
    (payload.workspaceId === undefined ||
      typeof payload.workspaceId === 'string') &&
    (payload.userId === undefined || typeof payload.userId === 'string') &&
    typeof payload.plan === 'string' &&
    Array.isArray(payload.features) &&
    payload.features.every(isFeatureAvailability) &&
    isValidIsoDate(payload.issuedAt) &&
    isValidIsoDate(payload.expiresAt)
  );
}

function matchesSubject(
  payload: SignedFeatureEntitlementPayload,
  expected: ExpectedFeatureEntitlementSubject
): boolean {
  if (expected.subjectType === 'workspace') {
    return (
      payload.subjectType === 'workspace' &&
      payload.subjectId === expected.workspaceId &&
      payload.workspaceId === expected.workspaceId &&
      payload.userId === expected.userId &&
      !hasKnownFeaturePlanConflict(expected.plan, payload.plan)
    );
  }

  return (
    payload.subjectType === 'plan' &&
    payload.subjectId.toLowerCase() === expected.plan.toLowerCase()
  );
}

export async function verifyFeatureEntitlement(
  entitlement: unknown,
  expected: ExpectedFeatureEntitlementSubject,
  now = new Date()
): Promise<FeatureEntitlementVerification> {
  const verification =
    await verifySignedLocalAuthorityEnvelope<FeatureEntitlementPayload>(
      entitlement,
      {
        expectedPurpose: 'feature-entitlement',
        now,
      }
    );
  if (verification.status === 'expired') {
    return { status: 'expired', features: [], plan: null, expiresAt: null };
  }
  if (
    verification.status !== 'valid' ||
    !verification.payload ||
    !isSignedFeatureEntitlementPayload(verification.payload) ||
    !matchesSubject(verification.payload, expected)
  ) {
    return { status: 'invalid', features: [], plan: null, expiresAt: null };
  }

  return {
    status: 'valid',
    features: verification.payload.features,
    plan: verification.payload.plan,
    expiresAt: verification.payload.expiresAt,
  };
}
